
import React, { useState, useEffect } from 'react';
import { storage } from '../services/storageService';
import { 
  Trophy, Medal, CalendarRange, Clock, 
  TrendingUp, Activity, BarChart2, Calendar, 
  ChevronDown, Search, Loader2, User
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from 'recharts';

interface AnalyticRecord {
  driverId: string;
  driverName: string;
  total_tasks: number;
  total_hours: number;
  avg_score: number;
}

const ManagerDashboard: React.FC = () => {
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('month');
  const [refDate, setRefDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<AnalyticRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await storage.getManagerAnalytics(period, refDate);
      // Ensure data is sorted by total_hours descending as requested
      const sorted = Array.isArray(res) ? res.sort((a, b) => b.total_hours - a.total_hours) : [];
      setData(sorted);
      setLoading(false);
    };
    fetchData();
  }, [period, refDate]);

  const getPeriodLabel = () => {
    const d = new Date(refDate);
    const y = d.getFullYear();
    if (period === 'year') return `${y} 年度总览`;
    if (period === 'quarter') return `${y} 第 ${Math.floor(d.getMonth() / 3) + 1} 季度`;
    return `${y}年 ${d.getMonth() + 1}月`;
  };

  const top3 = data.slice(0, 3);
  const rest = data.slice(3);

  const StatCard = ({ label, value, icon: Icon, color }: any) => (
    <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex items-center gap-4">
       <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6" />
       </div>
       <div>
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</div>
          <div className="text-2xl font-black text-slate-800">{value}</div>
       </div>
    </div>
  );

  // Aggregate stats
  const totalFleetHours = data.reduce((acc, curr) => acc + (curr.total_hours || 0), 0);
  const totalFleetTasks = data.reduce((acc, curr) => acc + (curr.total_tasks || 0), 0);
  const fleetEfficiency = totalFleetTasks > 0 ? (totalFleetHours / totalFleetTasks).toFixed(1) : '0.0';

  return (
    <div className="h-full flex flex-col gap-8 animate-in fade-in duration-700 p-8 overflow-y-auto scrollbar-hide">
      
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-800 italic uppercase tracking-tighter flex items-center gap-3">
             <Activity className="w-8 h-8 text-indigo-600" />
             经营分析驾驶舱
          </h2>
          <p className="text-slate-500 font-bold mt-1 uppercase tracking-widest text-[11px] pl-11">
            Manager Strategic View / 基于 D1 后端聚合计算 (Plan B)
          </p>
        </div>

        <div className="flex bg-white p-1.5 rounded-[24px] shadow-sm border border-slate-200">
           {['year', 'quarter', 'month'].map((p) => (
             <button
               key={p}
               onClick={() => setPeriod(p as any)}
               className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                 period === p 
                  ? 'bg-slate-900 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
               }`}
             >
               {p === 'year' ? '年度' : p === 'quarter' ? '季度' : '月度'}
             </button>
           ))}
           <div className="w-px h-6 bg-slate-200 mx-2 self-center"></div>
           <div className="relative flex items-center px-2">
              <input 
                type="date" 
                value={refDate} 
                onChange={(e) => setRefDate(e.target.value)}
                className="bg-transparent text-xs font-black text-slate-700 outline-none uppercase w-32 cursor-pointer"
              />
              <Calendar className="w-3.5 h-3.5 text-slate-400 pointer-events-none absolute right-2" />
           </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <StatCard 
            label="周期总工时 (Hours)" 
            value={totalFleetHours.toFixed(1)} 
            icon={Clock} 
            color="bg-indigo-50 text-indigo-600" 
         />
         <StatCard 
            label="周期总单量 (Orders)" 
            value={totalFleetTasks} 
            icon={BarChart2} 
            color="bg-emerald-50 text-emerald-600" 
         />
         <StatCard 
            label="平均单次耗时 (Hrs/Task)" 
            value={fleetEfficiency} 
            icon={TrendingUp} 
            color="bg-amber-50 text-amber-600" 
         />
      </div>

      <div className="flex items-center gap-4 py-4">
         <div className="h-px bg-slate-200 flex-1"></div>
         <span className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">{getPeriodLabel()} • 全员绩效排行榜</span>
         <div className="h-px bg-slate-200 flex-1"></div>
      </div>

      {/* Leaderboard Section */}
      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center min-h-[400px]">
           <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">正在从 D1 聚合海量数据...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center min-h-[400px] bg-white rounded-[40px] border border-slate-100 border-dashed">
           <Search className="w-12 h-12 text-slate-300 mb-4" />
           <p className="text-sm font-black text-slate-400 uppercase tracking-widest">该周期内无有效完工记录</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           
           {/* Top 3 Podium */}
           <div className="lg:col-span-1 flex flex-col gap-4">
              {top3.map((driver, idx) => (
                <div key={driver.driverId} className={`relative p-6 rounded-[32px] border flex items-center gap-4 overflow-hidden group transition-all hover:-translate-y-1 ${
                  idx === 0 ? 'bg-gradient-to-br from-yellow-50 to-white border-yellow-200 shadow-xl shadow-yellow-500/10' : 
                  idx === 1 ? 'bg-gradient-to-br from-slate-50 to-white border-slate-200 shadow-lg' :
                  'bg-white border-orange-100 shadow-sm'
                }`}>
                   <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black shadow-inner ${
                      idx === 0 ? 'bg-yellow-400 text-white' : 
                      idx === 1 ? 'bg-slate-300 text-white' : 'bg-orange-300 text-white'
                   }`}>
                      {idx === 0 ? <Trophy className="w-8 h-8" /> : idx === 1 ? '2' : '3'}
                   </div>
                   <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                         <div className="text-lg font-black text-slate-800 italic uppercase">{driver.driverName}</div>
                         {idx === 0 && <span className="text-[8px] bg-yellow-400 text-white px-2 py-0.5 rounded font-black uppercase tracking-wider">MVP</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs font-bold text-slate-500">
                         <span>⏱ {driver.total_hours.toFixed(1)} h</span>
                         <span>📦 {driver.total_tasks} 单</span>
                      </div>
                   </div>
                   {/* Progress Bar Background */}
                   <div className="absolute bottom-0 left-0 h-1 bg-current opacity-20" style={{ width: `${(driver.total_hours / (top3[0]?.total_hours || 1)) * 100}%`, color: idx === 0 ? '#EAB308' : idx === 1 ? '#94A3B8' : '#F97316' }}></div>
                </div>
              ))}

              {/* Chart for Top Drivers */}
              <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex-1 min-h-[200px]">
                 <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">头部工时贡献占比</div>
                 <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={top3} layout="vertical" margin={{top:0, left:0, right:30, bottom:0}}>
                       <XAxis type="number" hide />
                       <YAxis dataKey="driverName" type="category" width={60} tick={{fontSize: 10, fontWeight: 900, fill: '#64748B'}} axisLine={false} tickLine={false} />
                       <Tooltip cursor={{fill: 'transparent'}} contentStyle={{borderRadius: 12, border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)'}} />
                       <Bar dataKey="total_hours" barSize={20} radius={[0, 10, 10, 0]}>
                          {top3.map((entry, index) => (
                             <Cell key={`cell-${index}`} fill={index === 0 ? '#EAB308' : index === 1 ? '#94A3B8' : '#F97316'} />
                          ))}
                       </Bar>
                    </BarChart>
                 </ResponsiveContainer>
              </div>
           </div>

           {/* Full List Table */}
           <div className="lg:col-span-2 bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden flex flex-col">
              <div className="p-6 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                 <div className="text-sm font-black text-slate-800 uppercase italic">详细数据列表</div>
                 <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">按工时(Duration)降序排列</div>
              </div>
              <div className="overflow-y-auto flex-1 p-2">
                 <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-white z-10">
                       <tr>
                          <th className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">排名</th>
                          <th className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">驾驶员</th>
                          <th className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">总工时 (Hrs)</th>
                          <th className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">总单量</th>
                          <th className="p-4 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">平均评分</th>
                       </tr>
                    </thead>
                    <tbody className="text-sm">
                       {data.map((d, i) => (
                          <tr key={d.driverId} className="hover:bg-slate-50 transition-colors group border-b border-slate-50 last:border-0">
                             <td className="p-4 font-black text-slate-300 w-16">
                                <span className={`flex items-center justify-center w-6 h-6 rounded-lg ${i < 3 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                   {i + 1}
                                </span>
                             </td>
                             <td className="p-4 font-bold text-slate-700 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs text-slate-500 font-black uppercase">
                                   {d.driverName.charAt(0)}
                                </div>
                                {d.driverName}
                             </td>
                             <td className="p-4 font-black text-indigo-600 text-right font-mono text-base">
                                {d.total_hours?.toFixed(1) || '0.0'}
                             </td>
                             <td className="p-4 font-bold text-slate-600 text-right">
                                {d.total_tasks}
                             </td>
                             <td className="p-4 font-bold text-slate-600 text-right">
                                <span className={`px-2 py-1 rounded text-[10px] font-black ${d.avg_score >= 4.8 ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                   {d.avg_score?.toFixed(1) || '-'}
                                </span>
                             </td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
           </div>

        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;
